import React, { useState, useEffect, useRef } from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { FormControl, FormLabel, TextField, Slider, Checkbox, FormControlLabel } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { ImageInput } from './ImageInput';
import { useQwanta, uploadFiles } from 'qwanyx';

export const Carousel = ({
    name,
    showArrows = true,
    showDots = true,
    showCounter = true,
    height = 300,
    width = "100%",
    maxWidth,
    minWidth,
    flex,
    flexGrow,
    flexShrink,
    flexBasis,
    borderRadius = 0,
    borderWidth = 0,
    borderColor = '#ccc',
    hidden,
    background = 'transparent',
    qwantumId = "",
    children,
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
        id,
        actions: { setProp }
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { query, actions } = useEditor();
    const [_id] = useQwanta("_id");
    const s3url = process.env.REACT_APP_S3_URL_BASE;

    // Centralized carousel data persistence using useQwanta
    const [carouselData, setCarouselData] = useQwanta(qwantumId || `carousel-${id}`);
    const [shouldGoToLast, setShouldGoToLast] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [images, setImages] = useState([]);

    const isInitialMount = useRef(true);
    // Load images from persisted data
    useEffect(() => {
        if (carouselData) {
            try {
                const parsed = typeof carouselData === 'string' ? JSON.parse(carouselData) : carouselData;
                setImages(parsed.images || []);

                if (parsed.images && parsed.images.length > 0) {
                    if (shouldGoToLast) {
                        setCurrentSlide(parsed.images.length - 1);
                        //setShouldGoToLast(false); // Reset flag
                    } else {
                        setCurrentSlide(0); // Default to first
                    }
                } else {
                    setCurrentSlide(0);
                }
            } catch (e) {
                console.warn('Failed to parse carousel data:', e);
                setImages([]);
                setCurrentSlide(0);
            }
        } else {
            setImages([]);
            setCurrentSlide(0);
        }
    }, [carouselData, shouldGoToLast]);

    // Save carousel data
    const saveCarouselData = (newImages) => {
        const dataToSave = { images: newImages };
        const qId = qwantumId || `carousel-${id}`;

        if (qId && qId.trim() && setCarouselData) {
            // Use requestAnimationFrame to defer the persistence call
            requestAnimationFrame(() => {
                try {
                    setCarouselData(qId, dataToSave);
                } catch (error) {
                    console.warn('Carousel persistence failed:', error);
                }
            });
        }
    };

    // Handle image updates from ImageInput components
    const handleImageUpdate = (imageId, newData) => {
        const updatedImages = images.map(img =>
            img.id === imageId
                ? { ...img, ...JSON.parse(newData) }
                : img
        );
        setImages(updatedImages);
        saveCarouselData(updatedImages);
    };

    // Handle image deletion
    const handleImageDelete = (imageId) => {
        const updatedImages = images.filter(img => img.id !== imageId);
        setImages(updatedImages);
        saveCarouselData(updatedImages);
        // Adjust current slide if necessary
        if (currentSlide >= updatedImages.length && updatedImages.length > 0) {
            setCurrentSlide(updatedImages.length - 1);
        } else if (updatedImages.length === 0) {
            setCurrentSlide(0);
        }
    };

    const totalSlides = images.length;

    if (hidden) return null;

    const formatSize = (value) => {
        if (!value) return undefined;
        if (typeof value === 'string') return value;
        return `${value}px`;
    };

    const nextSlide = () => {
        if (totalSlides > 1) {
            setCurrentSlide((prev) => (prev + 1) % totalSlides);
        }
    };

    const prevSlide = () => {
        if (totalSlides > 1) {
            setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
        }
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    // File drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const hasFiles = e.dataTransfer.types.includes('Files');
        if (hasFiles && !isUploading) {
            setIsDragOver(true);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const hasFiles = e.dataTransfer.types.includes('Files');
        if (hasFiles && !isUploading) {
            e.dataTransfer.dropEffect = 'copy';
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragOver(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (isUploading) return;

        const files = Array.from(e.dataTransfer.files);
        const imageFiles = files.filter(file =>
            file.type.startsWith('image/') ||
            file.type === 'image/svg+xml' ||
            file.name.toLowerCase().endsWith('.svg')
        );

        if (imageFiles.length === 0) {
            if (files.length > 0) {
                alert('Please drop only image files (JPG, PNG, GIF, WebP, SVG)');
            }
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Upload files using _id as folder path
            const results = await uploadFiles(
                imageFiles.map(file => ({
                    file,
                    key: `${_id}/${file.name}`
                }))
            );

            // Create new image objects for the carousel
            const newImages = results.successful.map((key, index) => {
                const filename = key.split('/').pop();
                const imageUrl = `${s3url}/${_id}/${filename}`;

                return {
                    id: `img_${Date.now()}_${index}`,
                    url: imageUrl,
                    transforms: {
                        tx: 0,
                        ty: 0,
                        r: 0,
                        scale: 1,
                        flipx: false,
                        flipy: false
                    },
                    filename: filename
                };
            });

            // Add new images to existing ones
            const updatedImages = [...images, ...newImages];
            setImages(updatedImages);
            saveCarouselData(updatedImages);

            // Jump to the last uploaded image
            setTimeout(() => {
                goToSlide(updatedImages.length - 1);
            }, 0);

            // Report any failures
            if (results.failed.length > 0) {
                console.warn('Some uploads failed:', results.failed);
                alert(`${results.successful.length} images uploaded successfully. ${results.failed.length} failed.`);
            }

            setUploadProgress(100);

        } catch (error) {
            console.error('Upload error:', error);

            // Check if we have partial results
            if (error.results?.successful?.length > 0) {
                const partialImages = error.results.successful.map((key, index) => {
                    const filename = key.split('/').pop();
                    const imageUrl = `${s3url}/${_id}/${filename}`;

                    return {
                        id: `img_${Date.now()}_${index}`,
                        url: imageUrl,
                        transforms: {
                            tx: 0,
                            ty: 0,
                            r: 0,
                            scale: 1,
                            flipx: false,
                            flipy: false
                        },
                        filename: filename
                    };
                });

                const updatedImages = [...images, ...partialImages];
                setImages(updatedImages);
                saveCarouselData(updatedImages);
                setShouldGoToLast(true);
                // Jump to the last uploaded image (partial success)
                setCurrentSlide(updatedImages.length - 1);

                alert(`${error.results.successful.length} images uploaded. ${error.results.failed.length} failed.`);
            } else {
                alert('Upload failed. Please try again.');
            }
        } finally {
            setIsUploading(false);
            setTimeout(() => setUploadProgress(0), 2000);

        }
    };
    const getSelectionStyle = () => {
        if (isSelected) {
            return {
                outline: '2px dashed #2196f3',
                outlineOffset: '2px',
                backgroundColor: background === 'transparent' ? 'rgba(33, 150, 243, 0.05)' : undefined
            };
        }
        if (isHovered) {
            return {
                outline: '2px dashed #ff9800',
                outlineOffset: '1px',
                backgroundColor: background === 'transparent' ? 'rgba(255, 152, 0, 0.03)' : undefined
            };
        }
        return {};
    };

    const getDropOverlayStyle = () => {
        if (isUploading) {
            return {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                border: '3px dashed #4caf50',
                borderRadius: `${borderRadius}px`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#4caf50',
                fontSize: '16px',
                fontWeight: 'bold',
                zIndex: 100,
                pointerEvents: 'none'
            };
        }
        if (isDragOver) {
            return {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                border: '3px dashed #2196f3',
                borderRadius: `${borderRadius}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2196f3',
                fontSize: '18px',
                fontWeight: 'bold',
                zIndex: 100,
                pointerEvents: 'none'
            };
        }
        return { display: 'none' };
    };

    return (
        <div
            ref={ref => connect(drag(ref))}
            name={name}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
                position: 'relative',
                width: formatSize(width),
                height: formatSize(height),
                maxWidth: formatSize(maxWidth),
                minWidth: formatSize(minWidth),
                flex: flex,
                flexGrow: flexGrow,
                flexShrink: flexShrink,
                flexBasis: flexBasis,
                background,
                borderRadius: `${borderRadius}px`,
                border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                ...getSelectionStyle()
            }}
            {...props}
        >
            {/* Upload/Drop Overlay */}
            <div style={getDropOverlayStyle()}>
                {isUploading ? (
                    <>
                        <div>⬆️ Uploading images...</div>
                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div style={{ fontSize: '14px', marginTop: '8px' }}>
                                {uploadProgress}%
                            </div>
                        )}
                    </>
                ) : (
                    '📁 Drop images here'
                )}
            </div>

            {/* Slides Container */}
            <div style={{
                flex: 1,
                position: 'relative',
                overflow: 'hidden'
            }}>
                {totalSlides === 0 ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        color: '#999',
                        border: '2px dashed #ccc',
                        margin: '20px',
                        borderRadius: `${borderRadius}px`,
                        flexDirection: 'column',
                        gap: '10px'
                    }}>
                        <p>Drop image files here to create slides</p>
                        <p style={{ fontSize: '14px', opacity: 0.7 }}>
                            Supports JPG, PNG, GIF, WebP, SVG
                        </p>
                        {isUploading && (
                            <p style={{ fontSize: '12px', color: '#4caf50' }}>
                                Uploading images...
                            </p>
                        )}
                    </div>
                ) : (
                    // Render ImageInput components with carousel data
                    images.map((image, index) => {
                        const isVisible = index === currentSlide;

                        return (
                            <div
                                key={image.id}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    visibility: isVisible ? 'visible' : 'hidden',
                                    opacity: isVisible ? 1 : 0
                                }}
                            >
                                <ImageInput
                                    src={image.url}
                                    frameWidth="100%"
                                    hugImage={false}
                                    aspectRatio="auto"
                                    objectFit="contain"
                                    showBackground={false}
                                    borderWidth="0px"
                                    pictureX={image.transforms.tx}
                                    pictureY={image.transforms.ty}
                                    pictureRotation={image.transforms.r}
                                    pictureScale={image.transforms.scale}
                                    pictureFlipH={image.transforms.flipx}
                                    pictureFlipV={image.transforms.flipy}
                                    onSave={(data) => handleImageUpdate(image.id, data)}
                                    style={{
                                        width: '100%',
                                        height: '100%'
                                    }}
                                />
                                {/* Delete button */}
                                {isVisible && (
                                    <button
                                        onClick={() => handleImageDelete(image.id)}
                                        style={{
                                            position: 'absolute',
                                            top: '10px',
                                            left: '10px',
                                            backgroundColor: 'transparent',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            zIndex: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textShadow: '0 0 3px rgba(0,0,0,0.8)'
                                        }}
                                        title="Delete image"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Navigation Arrows */}
            {showArrows && totalSlides > 1 && !isUploading && (
                <>
                    <button
                        onClick={prevSlide}
                        style={{
                            position: 'absolute',
                            left: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            cursor: 'pointer',
                            fontSize: '20px',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <ChevronLeft />
                    </button>
                    <button
                        onClick={nextSlide}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            cursor: 'pointer',
                            fontSize: '20px',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <ChevronRight />
                    </button>
                </>
            )}

            {/* Dots Navigation */}
            {showDots && totalSlides > 1 && !isUploading && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '10px',
                    gap: '8px'
                }}>
                    {images.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                border: 'none',
                                backgroundColor: index === currentSlide ? '#333' : '#ccc',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                            }}
                        />
                    ))}
                </div>
            )}

            {/* Slide counter */}
            {showCounter && totalSlides > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    zIndex: 5
                }}>
                    {currentSlide + 1} / {totalSlides}
                </div>
            )}
        </div>
    );
};

const CarouselSettings = () => {
    const {
        actions: { setProp },
        name, showArrows, showDots, showCounter, height, width, maxWidth, minWidth,
        flex, flexGrow, flexShrink, flexBasis, borderRadius, borderWidth, borderColor, background, qwantumId
    } = useNode((node) => ({
        name: node.data.props.name,
        showArrows: node.data.props.showArrows,
        showDots: node.data.props.showDots,
        showCounter: node.data.props.showCounter,
        height: node.data.props.height,
        width: node.data.props.width,
        maxWidth: node.data.props.maxWidth,
        minWidth: node.data.props.minWidth,
        flex: node.data.props.flex,
        flexGrow: node.data.props.flexGrow,
        flexShrink: node.data.props.flexShrink,
        flexBasis: node.data.props.flexBasis,
        borderRadius: node.data.props.borderRadius,
        borderWidth: node.data.props.borderWidth,
        borderColor: node.data.props.borderColor,
        background: node.data.props.background,
        qwantumId: node.data.props.qwantumId
    }));

    const handleFlexChange = (prop, value) => {
        if (value === '') {
            setProp(props => props[prop] = undefined);
        } else {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                setProp(props => props[prop] = numValue);
            }
        }
    };

    return (
        <div>
            <FormControl fullWidth margin="normal">
                <FormLabel>Component Name</FormLabel>
                <TextField
                    value={name || ''}
                    onChange={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                    placeholder="Enter component name"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Quantum ID</FormLabel>
                <TextField
                    value={qwantumId || ''}
                    onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
                    size="small"
                    placeholder="Enter carousel quantum ID for persistence"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>How to use</FormLabel>
                <div style={{ fontSize: '12px', color: '#666', margin: '8px 0', lineHeight: '1.4' }}>
                    <p>• Drag & drop image files to upload and create slides</p>
                    <p>• Images are stored centrally in the carousel</p>
                    <p>• Click images to edit transforms (position, scale, etc.)</p>
                    <p>• Use delete button (×) to remove images</p>
                    <p>• Supports JPG, PNG, GIF, WebP, SVG formats</p>
                    <p>• All data persists with single quantum ID</p>
                </div>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Width</FormLabel>
                <TextField
                    value={width || ''}
                    onChange={(e) => setProp(props => props.width = e.target.value)}
                    size="small"
                    placeholder="100%, 500px, auto, or leave empty"
                />
            </FormControl>

            <FormControl size="small" component="fieldset" fullWidth margin="normal">
                <FormLabel component="legend">Height (px)</FormLabel>
                <Slider
                    value={height || 300}
                    step={10}
                    min={100}
                    max={800}
                    valueLabelDisplay="auto"
                    onChange={(_, value) => {
                        setProp(props => props.height = value);
                    }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Max Width</FormLabel>
                <TextField
                    value={maxWidth || ''}
                    onChange={(e) => setProp(props => props.maxWidth = e.target.value)}
                    size="small"
                    placeholder="800px, 100%, or leave empty"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Min Width</FormLabel>
                <TextField
                    value={minWidth || ''}
                    onChange={(e) => setProp(props => props.minWidth = e.target.value)}
                    size="small"
                    placeholder="200px, 50%, or leave empty"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Flex</FormLabel>
                <TextField
                    value={flex || ''}
                    onChange={(e) => handleFlexChange('flex', e.target.value)}
                    size="small"
                    placeholder="1, 0 1 auto, or leave empty"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Flex Grow</FormLabel>
                <TextField
                    value={flexGrow || ''}
                    onChange={(e) => handleFlexChange('flexGrow', e.target.value)}
                    size="small"
                    placeholder="0, 1, 2, or leave empty"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Flex Shrink</FormLabel>
                <TextField
                    value={flexShrink || ''}
                    onChange={(e) => handleFlexChange('flexShrink', e.target.value)}
                    size="small"
                    placeholder="1, 0, or leave empty"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Flex Basis</FormLabel>
                <TextField
                    value={flexBasis || ''}
                    onChange={(e) => setProp(props => props.flexBasis = e.target.value || undefined)}
                    size="small"
                    placeholder="auto, 300px, 50%, or leave empty"
                />
            </FormControl>

            <FormControl fullWidth margin="normal" component="fieldset">
                <FormLabel component="legend">Background</FormLabel>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                        type="color"
                        value={background === 'transparent' ? '#ffffff' : background || '#f5f5f5'}
                        onChange={(e) => setProp(props => props.background = e.target.value)}
                        style={{ width: '50px', height: '30px' }}
                    />
                    <button
                        onClick={() => setProp(props => props.background = 'transparent')}
                        style={{
                            padding: '5px 10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            backgroundColor: background === 'transparent' ? '#e3f2fd' : 'white',
                            cursor: 'pointer'
                        }}
                    >
                        No Background
                    </button>
                </div>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Border Radius (px)</FormLabel>
                <TextField
                    value={borderRadius || 0}
                    onChange={(e) => setProp(props => props.borderRadius = parseInt(e.target.value) || 0)}
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 50 }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Border Width (px)</FormLabel>
                <TextField
                    value={borderWidth || 0}
                    onChange={(e) => setProp(props => props.borderWidth = parseInt(e.target.value) || 0)}
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 10 }}
                />
            </FormControl>

            <FormControl fullWidth margin="normal" component="fieldset">
                <FormLabel component="legend">Border Color</FormLabel>
                <input
                    type="color"
                    value={borderColor || '#ccc'}
                    onChange={(e) => setProp(props => props.borderColor = e.target.value)}
                />
            </FormControl>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={showArrows}
                        onChange={(e) => setProp(props => props.showArrows = e.target.checked)}
                    />
                }
                label="Show Arrows"
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={showDots}
                        onChange={(e) => setProp(props => props.showDots = e.target.checked)}
                    />
                }
                label="Show Dots"
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={showCounter}
                        onChange={(e) => setProp(props => props.showCounter = e.target.checked)}
                    />
                }
                label="Show Counter"
            />
        </div>
    );
};

Carousel.craft = {
    props: {
        name: "",
        showArrows: true,
        showDots: true,
        showCounter: true,
        height: 300,
        width: "100%",
        maxWidth: null,
        minWidth: null,
        flex: null,
        flexGrow: null,
        flexShrink: null,
        flexBasis: null,
        borderRadius: 0,
        borderWidth: 0,
        borderColor: '#ccc',
        background: 'transparent',
        qwantumId: ""
    },
    rules: {
        canDrag: () => true,
    },
    related: {
        settings: CarouselSettings
    },
    isCanvas: true  // Changed to true to make it droppable
};

Carousel.displayName = 'Carousel';